import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProviderCard, type ProviderCardData } from "@/components/services/ProviderCard";
import { supabase } from "@/integrations/supabase/client";

/**
 * Sprint 3.1 — Renders providers whose `spotlight_until` has not expired,
 * shuffled per session. Hidden when no provider qualifies.
 */
export function FeaturedProvidersRail({ limit = 8 }: { limit?: number }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ProviderCardData[] | null>(null);

  // Stable per-mount seed for shuffling.
  const seed = useMemo(() => Math.random(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nowIso = new Date().toISOString();
      const { data: profiles } = await supabase
        .from("provider_profiles")
        .select("user_id, headline_en, headline_fr, expertise_areas, languages, rating_avg, rating_count, tier")
        .gt("spotlight_until", nowIso)
        .eq("trust_status", "verified")
        .eq("lifecycle_status", "active");
      if (cancelled) return;
      const list = profiles ?? [];
      if (list.length === 0) {
        setItems([]);
        return;
      }
      const ids = list.map((p) => p.user_id);
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url")
        .in("id", ids);
      const profMap = new Map((profs ?? []).map((p) => [p.id, p]));
      const cards: ProviderCardData[] = list.map((p: any) => {
        const u = profMap.get(p.user_id) as any;
        return {
          user_id: p.user_id,
          first_name: u?.first_name ?? null,
          last_name: u?.last_name ?? null,
          avatar_url: u?.avatar_url ?? null,
          headline: p.headline_en ?? p.headline_fr ?? null,
          expertise_areas: p.expertise_areas ?? null,
          languages: p.languages ?? null,
          rating_avg: p.rating_avg ?? null,
          rating_count: p.rating_count ?? null,
          tier: p.tier ?? null,
          trust_status: "verified",
        };
      });
      // Deterministic shuffle by seed.
      const shuffled = cards
        .map((c, i) => ({ c, k: Math.sin(seed * 1e6 + i) }))
        .sort((a, b) => a.k - b.k)
        .map((x) => x.c)
        .slice(0, limit);
      setItems(shuffled);
    })();
    return () => {
      cancelled = true;
    };
  }, [seed, limit]);

  if (items === null || items.length === 0) return null;

  return (
    <section className="container py-10" aria-label={t("home_spotlight.title")}>
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">{t("home_spotlight.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("home_spotlight.subtitle")}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((p) => (
          <ProviderCard key={p.user_id} p={p} />
        ))}
      </div>
    </section>
  );
}